/**
 * Granola Note Schemas — the wire shape of a note as Granola's `getNote` returns it.
 *
 * This is the shape U1's backend client parses the Granola API response into,
 * so a Granola field rename surfaces as a validation error rather than an
 * undefined read three services downstream.
 *
 * ⚠️ **NO TRANSCRIPT FIELD BELONGS HERE, EVER.** R7 / KTD1 of the meeting-lane
 * plan settle that the lane reads the note's summary only and never fetches a
 * transcript — the summary is 11.6× cheaper on the measured fixture and
 * already shaped the way extraction needs. Adding a transcript field to this
 * schema would make it trivial for a later change to start reading it; don't.
 *
 * Verified against `runs/meeting/*\/granola.json` (the `note` object) across
 * all eight fixture calls: `calendar_event` is `null` on notes with no linked
 * calendar event (e.g. `2026-07-10-mobile-app-review`) and an object
 * otherwise; `summary_markdown` and `summary_text` are both present on every
 * fixture observed so far, but the API contract only guarantees one of them,
 * which is what the refine below enforces.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-08-30-0955-feat-granola-summary-meeting-lane-plan.md
 */

import { z } from 'zod'

/**
 * Named so U1's test can assert on it rather than a magic string, and so the
 * job (U8) can distinguish "no summary at all" from any other parse failure.
 */
export const GRANOLA_NOTE_NO_SUMMARY_ERROR = 'granola_note_missing_summary'

const granolaNotePersonSchema = z
  .object({
    name: z.string().optional(),
    email: z.string().optional()
  })
  .passthrough()

const granolaNoteFolderMembershipSchema = z
  .object({
    object: z.string().optional(),
    id: z.string().optional(),
    name: z.string().optional(),
    parent_folder_id: z.string().nullable().optional(),
    space_id: z.string().optional()
  })
  .passthrough()

const granolaNoteSpaceMembershipSchema = z
  .object({
    object: z.string().optional(),
    id: z.string().optional(),
    name: z.string().optional()
  })
  .passthrough()

const granolaNoteCalendarEventSchema = z
  .object({
    event_title: z.string().optional(),
    invitees: z.array(granolaNotePersonSchema).optional()
  })
  .passthrough()

export const granolaNoteSchema = z
  .object({
    id: z.string(),
    object: z.string(),
    title: z.string(),
    web_url: z.string(),
    owner: granolaNotePersonSchema,
    created_at: z.string(),
    updated_at: z.string(),
    calendar_event: granolaNoteCalendarEventSchema.nullable().optional(),
    attendees: z.array(granolaNotePersonSchema).default([]),
    folder_membership: z.array(granolaNoteFolderMembershipSchema).default([]),
    space_membership: z.array(granolaNoteSpaceMembershipSchema).default([]),
    // Neither field is guaranteed alone — see the refine below (R7 / U1's
    // "missing both" test scenario). Structure parsing (U2) prefers
    // `summary_markdown` and falls back to `summary_text`.
    summary_text: z.string().optional(),
    summary_markdown: z.string().optional()
  })
  .superRefine((note, ctx) => {
    if (!note.summary_text && !note.summary_markdown) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['summary_markdown'],
        message: GRANOLA_NOTE_NO_SUMMARY_ERROR
      })
    }
  })

export type GranolaNote = z.infer<typeof granolaNoteSchema>
